import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Users, Flame, Phone, Mail, Calendar, MapPin, Download, IndianRupee, TrendingUp, Sparkles, Clock, Trash2, MessageSquare, CheckCircle, XCircle, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminInstantItineraries = () => {
  const [leads, setLeads] = useState([]);
  const [hotLeads, setHotLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalLeads: 0, totalHotLeads: 0, totalRevenue: 0, totalCost: 0 });
  const [adminRole, setAdminRole] = useState('');

  const auth = useCallback(() => {
    const stored = localStorage.getItem('adminAuth');
    return stored ? { Authorization: `Basic ${stored}` } : {};
  }, []);

  useEffect(() => {
    setAdminRole(localStorage.getItem('adminRole') || '');
  }, []);

  // State for pending/failed requests
  const [pendingRequests, setPendingRequests] = useState([]);
  const [pendingStats, setPendingStats] = useState({ total: 0, pending_payment: 0, generation_failed: 0 });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch spin wheel leads
      const leadsRes = await fetch(`${API_URL}/api/admin/spin-wheel-leads`, { headers: auth() });
      if (leadsRes.ok) {
        const leadsData = await leadsRes.json();
        setLeads(leadsData);
      }

      // Fetch hot leads (completed itineraries)
      const hotLeadsRes = await fetch(`${API_URL}/api/admin/instant-itinerary-leads`, { headers: auth() });
      if (hotLeadsRes.ok) {
        const hotLeadsData = await hotLeadsRes.json();
        setHotLeads(hotLeadsData.leads || []);
        setStats(hotLeadsData.stats || { totalLeads: 0, totalHotLeads: 0, totalRevenue: 0, totalCost: 0 });
      }

      // Fetch pending/failed requests
      const pendingRes = await fetch(`${API_URL}/api/admin/pending-itinerary-requests`, { headers: auth() });
      if (pendingRes.ok) {
        const pendingData = await pendingRes.json();
        // Filter to only show non-completed requests
        const nonCompleted = (pendingData.requests || []).filter(r => r.status !== 'completed');
        setPendingRequests(nonCompleted);
        setPendingStats(pendingData.stats || { total: 0, pending_payment: 0, generation_failed: 0 });
      }
    } catch (err) {
      toast.error('Failed to fetch data');
    }
    setLoading(false);
  }, [auth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isSuper = adminRole === 'super';
  
  // Export filter state
  const [exportYear, setExportYear] = useState('');
  const [exportMonth, setExportMonth] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteYear, setDeleteYear] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Update status for spin wheel lead
  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/spin-wheel-leads/${leadId}/status?new_status=${newStatus}`, {
        method: 'PUT',
        headers: auth()
      });
      if (res.ok) {
        toast.success('Lead status updated');
        fetchData();
      } else {
        toast.error('Failed to update status');
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // Delete spin wheel lead
  const deleteLead = async (leadId) => {
    if (!window.confirm('Delete this lead? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/spin-wheel-leads/${leadId}`, {
        method: 'DELETE',
        headers: auth()
      });
      if (res.ok) {
        toast.success('Lead deleted');
        fetchData();
      } else {
        toast.error('Failed to delete lead');
      }
    } catch (err) {
      toast.error('Failed to delete lead');
    }
  };

  // Update status for hot lead (instant itinerary)
  const updateHotLeadStatus = async (orderId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/instant-itinerary-leads/${orderId}/status?new_status=${newStatus}`, {
        method: 'PUT',
        headers: auth()
      });
      if (res.ok) {
        toast.success('Lead status updated');
        fetchData();
      } else {
        toast.error('Failed to update status');
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // Delete hot lead
  const deleteHotLead = async (orderId) => {
    if (!window.confirm('Delete this hot lead and its itinerary? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/instant-itinerary-leads/${orderId}`, {
        method: 'DELETE',
        headers: auth()
      });
      if (res.ok) {
        toast.success('Hot lead deleted');
        fetchData();
      } else {
        toast.error('Failed to delete hot lead');
      }
    } catch (err) {
      toast.error('Failed to delete hot lead');
    }
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'contacted': return 'bg-yellow-100 text-yellow-800';
      case 'converted': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  // Download transactions CSV with filters
  const downloadTransactionsCSV = async () => {
    try {
      toast.loading('Generating transaction report...');
      let url = `${API_URL}/api/admin/instant-itinerary-transactions/export`;
      const params = new URLSearchParams();
      if (exportYear) params.append('year', exportYear);
      if (exportMonth) params.append('month', exportMonth);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await fetch(url, { headers: auth() });
      if (res.ok) {
        const blob = await res.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        const filterSuffix = exportYear ? `_${exportYear}${exportMonth ? `-${exportMonth}` : ''}` : '';
        a.download = `instant_itinerary_transactions${filterSuffix}_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
        toast.dismiss();
        toast.success('Transaction report downloaded!');
        setShowExportModal(false);
      } else {
        toast.dismiss();
        toast.error('Failed to download report');
      }
    } catch (err) {
      toast.dismiss();
      toast.error('Failed to download report');
    }
  };

  // Delete old transactions (Super Admin only)
  const deleteOldTransactions = async () => {
    if (!deleteYear || !isSuper) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/instant-itinerary-transactions/delete-old?year=${deleteYear}`, {
        method: 'DELETE',
        headers: auth()
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowDeleteModal(false);
        setDeleteYear('');
        fetchData(); // Refresh data
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete transactions');
      }
    } catch (err) {
      toast.error('Failed to delete transactions');
    } finally {
      setDeleting(false);
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-amber-600" />
            Instant Itineraries
          </h1>
          <p className="text-gray-500">Track leads from the AI Itinerary feature</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowExportModal(true)} variant="outline" className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export Transactions
          </Button>
          {isSuper && (
            <Button onClick={() => setShowDeleteModal(true)} variant="outline" className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50">
              <Trash2 className="w-4 h-4" />
              Delete Old Data
            </Button>
          )}
          <Button onClick={fetchData} variant="outline">Refresh</Button>
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5" />
                Export Transactions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-500">
                Export transaction data with GST breakdown. Leave filters empty to export all data.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                  <select 
                    value={exportYear} 
                    onChange={(e) => setExportYear(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="">All Years</option>
                    {[currentYear, currentYear - 1, currentYear - 2].map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                  <select 
                    value={exportMonth} 
                    onChange={(e) => setExportMonth(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                    disabled={!exportYear}
                  >
                    <option value="">All Months</option>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                      <option key={m} value={m}>{new Date(2000, m-1).toLocaleString('en', {month: 'long'})}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setShowExportModal(false)}>Cancel</Button>
                <Button onClick={downloadTransactionsCSV} className="bg-amber-600 hover:bg-amber-700">
                  <Download className="w-4 h-4 mr-2" /> Download CSV
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Modal (Super Admin only) */}
      {showDeleteModal && isSuper && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Delete Old Transaction Data
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-500">
                <strong>Warning:</strong> This will permanently delete all transaction data from the selected year and earlier. This action cannot be undone.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Delete data from year and earlier</label>
                <select 
                  value={deleteYear} 
                  onChange={(e) => setDeleteYear(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">Select Year</option>
                  {[currentYear - 1, currentYear - 2, currentYear - 3].map(y => (
                    <option key={y} value={y}>{y} and earlier</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => { setShowDeleteModal(false); setDeleteYear(''); }}>Cancel</Button>
                <Button 
                  onClick={deleteOldTransactions} 
                  disabled={!deleteYear || deleting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {deleting ? 'Deleting...' : 'Delete Permanently'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Spin Wheel Leads</p>
                <p className="text-2xl font-bold">{leads.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Flame className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Hot Leads (Paid)</p>
                <p className="text-2xl font-bold">{hotLeads.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="leads" className="w-full">
        <TabsList>
          <TabsTrigger value="leads" className="flex items-center gap-2">
            <Users className="w-4 h-4" /> Leads ({leads.length})
          </TabsTrigger>
          <TabsTrigger value="hot-leads" className="flex items-center gap-2">
            <Flame className="w-4 h-4" /> Hot Leads ({hotLeads.length})
          </TabsTrigger>
          {pendingRequests.length > 0 && (
            <TabsTrigger value="failed" className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-4 h-4" /> 
              Failed/Pending ({pendingRequests.length})
            </TabsTrigger>
          )}
        </TabsList>

        {/* Spin Wheel Leads Tab */}
        <TabsContent value="leads">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Spin Wheel Leads
              </CardTitle>
              <p className="text-sm text-gray-500">People who filled the form after spinning the wheel</p>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : leads.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No leads yet</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-3 font-medium">Name</th>
                        <th className="text-left p-3 font-medium">Contact</th>
                        <th className="text-left p-3 font-medium">Won Code</th>
                        <th className="text-left p-3 font-medium">Discount</th>
                        <th className="text-left p-3 font-medium">Status</th>
                        <th className="text-left p-3 font-medium">Date</th>
                        <th className="text-left p-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {leads.map((lead, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="p-3 font-medium">{lead.name}</td>
                          <td className="p-3">
                            <div className="flex flex-col gap-1">
                              <span className="flex items-center gap-1 text-gray-600">
                                <Mail className="w-3 h-3" /> {lead.email}
                              </span>
                              <span className="flex items-center gap-1 text-gray-600">
                                <Phone className="w-3 h-3" /> {lead.phone}
                              </span>
                            </div>
                          </td>
                          <td className="p-3">
                            <span className="bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs font-medium">
                              {lead.won_code}
                            </span>
                          </td>
                          <td className="p-3 font-semibold text-green-600">{lead.won_discount}% OFF</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(lead.status || 'new')}`}>
                              {(lead.status || 'new').charAt(0).toUpperCase() + (lead.status || 'new').slice(1)}
                            </span>
                          </td>
                          <td className="p-3 text-gray-500 text-xs">{formatDate(lead.created_at)}</td>
                          <td className="p-3">
                            <div className="flex flex-wrap gap-1">
                              {lead.phone && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs"
                                  onClick={() => window.open(`https://wa.me/${lead.phone.replace(/[^0-9]/g, '')}`, '_blank')}
                                  title="Chat on WhatsApp"
                                >
                                  <MessageSquare className="w-3 h-3" />
                                </Button>
                              )}
                              {lead.status !== 'contacted' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs text-yellow-600 hover:bg-yellow-50"
                                  onClick={() => updateLeadStatus(lead.id, 'contacted')}
                                  title="Mark as Contacted"
                                >
                                  <Phone className="w-3 h-3" />
                                </Button>
                              )}
                              {lead.status !== 'converted' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs text-green-600 hover:bg-green-50"
                                  onClick={() => updateLeadStatus(lead.id, 'converted')}
                                  title="Mark as Converted"
                                >
                                  <CheckCircle className="w-3 h-3" />
                                </Button>
                              )}
                              {lead.status !== 'closed' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs text-gray-600 hover:bg-gray-100"
                                  onClick={() => updateLeadStatus(lead.id, 'closed')}
                                  title="Close"
                                >
                                  <XCircle className="w-3 h-3" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs text-red-600 hover:bg-red-50"
                                onClick={() => deleteLead(lead.id)}
                                title="Delete"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Hot Leads Tab */}
        <TabsContent value="hot-leads">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Flame className="w-5 h-5 text-orange-600" />
                Hot Leads - Paid Itineraries
              </CardTitle>
              <p className="text-sm text-gray-500">Customers who paid and received their AI itinerary</p>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : hotLeads.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No paid itineraries yet</div>
              ) : (
                <div className="space-y-4">
                  {hotLeads.map((lead, i) => (
                    <div 
                      key={i} 
                      className={`border rounded-lg p-4 transition-colors ${
                        lead.is_returning_converted 
                          ? 'border-2 border-purple-400 bg-purple-50/50' 
                          : 'hover:border-amber-300'
                      }`}
                    >
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        {/* Customer Info */}
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className="bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full text-xs font-medium">
                              HOT LEAD
                            </span>
                            {lead.is_returning_converted && (
                              <span className="bg-purple-200 text-purple-800 px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1">
                                <RefreshCw className="w-3 h-3" /> RETURNING CUSTOMER
                              </span>
                            )}
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(lead.status || 'new')}`}>
                              {(lead.status || 'new').charAt(0).toUpperCase() + (lead.status || 'new').slice(1)}
                            </span>
                            {lead.discount_code && (
                              <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs">
                                Used: {lead.discount_code}
                              </span>
                            )}
                          </div>
                          <h3 className="font-semibold text-lg">{lead.customer_name || 'Customer'}</h3>
                          <div className="flex flex-wrap gap-3 text-sm text-gray-600 mt-1">
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" /> {lead.customer_email}
                            </span>
                            {lead.customer_phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {lead.customer_phone}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Payment & Cost Info */}
                        <div className="flex flex-col items-end gap-1">
                          <div className="text-right">
                            <span className="text-sm text-gray-500">Paid:</span>
                            <span className="ml-2 text-lg font-bold text-green-600">₹{lead.amount_paid}</span>
                          </div>
                          {isSuper && lead.estimated_cost !== undefined && (
                            <div className="text-right text-sm">
                              <span className="text-gray-500">AI Cost:</span>
                              <span className="ml-2 font-medium text-red-500">₹{lead.estimated_cost?.toFixed(2)}</span>
                              <span className="ml-2 text-green-600">
                                (Profit: ₹{(lead.profit ?? ((lead.net_amount || lead.amount_paid) - (lead.estimated_cost || 0))).toFixed(2)})
                              </span>
                            </div>
                          )}
                          <div className="flex items-center gap-1 text-xs text-gray-400">
                            <Clock className="w-3 h-3" /> {formatDate(lead.created_at)}
                          </div>
                        </div>
                      </div>

                      {/* Itinerary Details */}
                      {lead.itinerary && (
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="font-medium text-amber-700 mb-2">{lead.itinerary.title}</h4>
                          <div className="flex flex-wrap gap-4 text-sm">
                            <span className="flex items-center gap-1 text-gray-600">
                              <MapPin className="w-4 h-4 text-amber-500" />
                              {lead.form?.destinations?.join(', ') || 'N/A'}
                            </span>
                            <span className="flex items-center gap-1 text-gray-600">
                              <Calendar className="w-4 h-4 text-amber-500" />
                              {lead.form?.days || '?'} Days
                            </span>
                            <span className="text-gray-600">
                              {lead.form?.adults || 0} Adults, {lead.form?.kids || 0} Kids
                            </span>
                            <span className="text-gray-600">
                              {lead.form?.trip_type || 'N/A'} • {lead.form?.hotel_preference || 'Any'} Hotels
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="mt-4 pt-4 border-t flex flex-wrap gap-2">
                        {lead.customer_phone && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 hover:bg-green-50"
                            onClick={() => window.open(`https://wa.me/${lead.customer_phone.replace(/[^0-9]/g, '')}`, '_blank')}
                          >
                            <MessageSquare className="w-4 h-4 mr-1" /> Chat
                          </Button>
                        )}
                        {lead.status !== 'contacted' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-yellow-600 hover:bg-yellow-50"
                            onClick={() => updateHotLeadStatus(lead.order_id, 'contacted')}
                          >
                            <Phone className="w-4 h-4 mr-1" /> Mark Contacted
                          </Button>
                        )}
                        {lead.status !== 'converted' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 hover:bg-green-50"
                            onClick={() => updateHotLeadStatus(lead.order_id, 'converted')}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" /> Converted
                          </Button>
                        )}
                        {lead.status !== 'closed' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-gray-600 hover:bg-gray-100"
                            onClick={() => updateHotLeadStatus(lead.order_id, 'closed')}
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Close
                          </Button>
                        )}
                        {lead.itinerary?.pdf_url && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => window.open(`${API_URL}${lead.itinerary.pdf_url}`, '_blank')}
                          >
                            <Download className="w-4 h-4 mr-1" /> Download PDF
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => deleteHotLead(lead.order_id)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" /> Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Failed/Pending Requests Tab */}
        <TabsContent value="failed">
          <Card className="border-red-200">
            <CardHeader className="bg-red-50">
              <CardTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" />
                Failed & Pending Requests
              </CardTitle>
              <p className="text-sm text-red-600">
                Customers who started but couldn't complete their itinerary - contact them to help!
              </p>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : pendingRequests.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No failed or pending requests</div>
              ) : (
                <div className="divide-y">
                  {pendingRequests.map((req, i) => (
                    <div key={i} className={`p-4 ${
                      req.status === 'generation_failed' ? 'bg-red-50' : 
                      req.status === 'pending_payment' ? 'bg-yellow-50' : 'bg-gray-50'
                    }`}>
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              req.status === 'generation_failed' ? 'bg-red-200 text-red-800' :
                              req.status === 'pending_payment' ? 'bg-yellow-200 text-yellow-800' :
                              req.status === 'generating' ? 'bg-blue-200 text-blue-800' :
                              'bg-gray-200 text-gray-800'
                            }`}>
                              {req.status === 'generation_failed' ? 'GENERATION FAILED' :
                               req.status === 'pending_payment' ? 'PAYMENT PENDING' :
                               req.status === 'generating' ? 'GENERATING...' :
                               req.status.toUpperCase()}
                            </span>
                          </div>
                          <h3 className="font-semibold text-lg">{req.customer_name || 'Customer'}</h3>
                          <div className="flex flex-wrap gap-3 text-sm text-gray-600 mt-1">
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" /> {req.customer_email}
                            </span>
                            {req.customer_phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {req.customer_phone}
                              </span>
                            )}
                          </div>
                          {req.gen_error && (
                            <div className="mt-2 p-2 bg-red-100 rounded text-xs text-red-700">
                              <strong>Error:</strong> {req.gen_error}
                            </div>
                          )}
                        </div>
                        <div className="text-right text-sm text-gray-500">
                          <div className="flex items-center gap-1 justify-end">
                            <Clock className="w-3 h-3" /> {formatDate(req.created_at)}
                          </div>
                        </div>
                      </div>

                      {/* Trip Details */}
                      {req.form && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-sm font-medium text-gray-700 mb-2">Trip Request:</p>
                          <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4 text-amber-500" />
                              {req.form.destinations?.join(', ') || 'N/A'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4 text-amber-500" />
                              {req.form.days || '?'} Days
                            </span>
                            <span>{req.form.adults || 0} Adults, {req.form.kids || 0} Kids</span>
                            <span>{req.form.trip_type || 'N/A'}</span>
                          </div>
                          {req.form.special_requests && (
                            <div className="mt-2 p-2 bg-gray-100 rounded text-sm">
                              <strong>Special Requests:</strong> {req.form.special_requests}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="mt-3 pt-3 border-t flex flex-wrap gap-2">
                        {req.customer_phone && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 hover:bg-green-50"
                            onClick={() => window.open(`https://wa.me/${req.customer_phone.replace(/[^0-9]/g, '')}`, '_blank')}
                          >
                            <MessageSquare className="w-4 h-4 mr-1" /> WhatsApp
                          </Button>
                        )}
                        {req.customer_email && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(`mailto:${req.customer_email}?subject=Your Travel Itinerary Request&body=Hi ${req.customer_name || 'there'},\n\nWe noticed you had trouble with your itinerary request for ${req.form?.destinations?.join(', ') || 'your trip'}. We would love to help you complete it.\n\nPlease let us know how we can assist.\n\nBest regards,\nThe Navkar Travels Team`)}
                          >
                            <Mail className="w-4 h-4 mr-1" /> Email
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminInstantItineraries;
